<template>
    <div class="recent-order-details">
        <div class="main-order-info">
            <div class="flex-column order-numbers">
                <h4 class="order-number" v-if="order.web_order_id">Web Confirmation #{{ order.web_order_id }}</h4>
                <h4 class="order-number" v-if="order.code">{{order.order_type}} #{{ order.code }}</h4>
            </div>
            <div class="order-date" v-if="orderDate">
                <p class="label">{{order.order_type}} Date:</p>
                <p> {{ orderDate }}</p>
            </div>
            <div class="order-total" v-if="order.total">
                <p class="label">Total:</p>
                <p> ${{ formatPrice(order.total) }}</p>
            </div>
        </div>
        <p class="order-status text-inStockGreen" v-if="order.status">{{order.order_type}} {{order.status}}</p>
        <div class="order-products">
            <div class="image-container" v-for="(item, index) of orderItemImages" :key="`recent_order_item_${index}`">
                <media v-bind="item.props" :id="`order_item_image_${index}`"/>
            </div>
            <div v-if="order.count > 4" id="more_items_count" class="more-products bg-rothcoRed text-rothcoWhite">+{{ order.count - 4 }}</div>
        </div>
        <div class="ctas-container">
            <RothcoCta id="return_button" v-if="orderIsReceived" :href="route('return.show', [order.encrypted_order_id])" class="secondary">
                Initiate Return
            </RothcoCta>
            <RothcoCta id="tracking_button" v-else-if="props.order.tracking_number" class="secondary" :href="props.order.tracking_url" target="_blank" inertia external>
                Track Order
            </RothcoCta>
            <RothcoCta class="primary" :href="route('order.details', [order.order_id])">
                View Details
            </RothcoCta>
        </div>
    </div>
</template>

<script setup lang="ts">
    import moment from "moment";
    import { ref, onMounted, onBeforeUnmount, computed } from "vue";
    import type { PropType } from "vue";
    import { type Order } from "@interfaces/Order";
    import Media from "@/components/Media.vue";
    import RothcoCta from "./RothcoCta.vue";
    import { formatPrice } from "@/helpers/composables/usePriceFormatting"
    import { useDateFormat } from "@vueuse/core";
    import { route } from "ziggy-js";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import {
        productUploadImageResponsiveFromFilename,
        PRODUCT_IMAGE_SIZES_ORDER_TILE
    } from "@/helpers/productUploadImageUrl";

    let props = defineProps({
        order: {
            type: Object as PropType<Order>,
            required: true,
            default: () => {
                return {};
            }
        }
    });

    function getTrackingUrl($trackingNumber) {
        console.log('getTrackingUrl', $trackingNumber.length)
        if ($trackingNumber) {
            if ($trackingNumber.length == 18) {
                return `https://www.ups.com/track?loc=en_US&tracknum=${$trackingNumber}`;
            } else if ($trackingNumber.length == 12) {
                return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${$trackingNumber}`;
            } else if ($trackingNumber.length >= 22) {
                return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${$trackingNumber}`;
            }
        }
        return false;
    }

    let orderDate = computed(() => {
        if (props.order.created_at === null) {
            return "";
        }
        let date = useDateFormat(
            props.order.created_at,
            "MM/DD/YYYY"
        ).value.replace(/\"/g, "");
        return date;
    });

    let orderItemImages = computed(() => {
        return props.order.order_items.slice(0, 4).map((item, index) => {
            const fn = item.item_variation?.primary_image?.image_filename;
            if (!fn) {
                return {
                    item: item.item_variation?.b2b_identifier,
                    props: {
                        defaultMediaType: "image",
                        alt: item.item_variation?.b2b_identifier,
                        media: comingSoonImages().small
                    }
                };
            }
            const pack = productUploadImageResponsiveFromFilename(
                fn,
                PRODUCT_IMAGE_SIZES_ORDER_TILE
            );
            return {
                item: item.item_variation?.b2b_identifier,
                props: {
                    defaultMediaType: "image",
                    alt: item.item_variation?.b2b_identifier,
                    media: pack.src,
                    srcSet: pack.srcSet,
                    sizes: pack.sizes
                }
            };
        });
    });

    const orderIsReceived = computed(() => props.order.status == "Closed");
</script>
